/**
 * The Invoice empty state's featured icon, exported straight from the
 * Figma (OR-1449).
 *
 * This is the whole treatment, not just a glyph: the white rounded square,
 * its hairline border and the three stacked drop shadows plus the inner
 * shadow. So it replaces atlas's `EmptyState.FeaturedIcon` rather than
 * being passed to it — wrapping it would double the container.
 *
 * Kept as inline SVG instead of an asset file so it inherits nothing from
 * an <img> pipeline and the filter travels with it. The filter id is the
 * Figma export's, unique enough not to collide with anything else on the
 * page.
 */
export function InvoiceEmptyIcon({ className }: { className?: string }) {
    return (
        <svg
            width="52"
            height="56"
            viewBox="0 0 52 56"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            className={className}
        >
            <g filter="url(#invoice-empty-icon-shadow)">
                <path
                    d="M5 10C5 5.58172 8.58172 2 13 2H37C41.4183 2 45 5.58172 45 10V34C45 38.4183 41.4183 42 37 42H13C8.58172 42 5 38.4183 5 34V10Z"
                    fill="white"
                />
                <path
                    d="M37 1.5C41.6944 1.5 45.5 5.30558 45.5 10V34C45.5 38.6944 41.6944 42.5 37 42.5H13C8.30558 42.5 4.5 38.6944 4.5 34V10C4.5 5.30558 8.30558 1.5 13 1.5H37Z"
                    stroke="black"
                    strokeOpacity="0.1"
                />
                <path
                    d="M27 21H21M23 25H21M29 17H21M33 16.8V27.2C33 28.8802 33 29.7202 32.673 30.362C32.3854 30.9265 31.9265 31.3854 31.362 31.673C30.7202 32 29.8802 32 28.2 32H21.8C20.1198 32 19.2798 32 18.638 31.673C18.0735 31.3854 17.6146 30.9265 17.327 30.362C17 29.7202 17 28.8802 17 27.2V16.8C17 15.1198 17 14.2798 17.327 13.638C17.6146 13.0735 18.0735 12.6146 18.638 12.327C19.2798 12 20.1198 12 21.8 12H28.2C29.8802 12 30.7202 12 31.362 12.327C31.9265 12.6146 32.3854 13.0735 32.673 13.638C33 14.2798 33 15.1198 33 16.8Z"
                    stroke="#839FD1"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </g>
            <defs>
                <filter
                    id="invoice-empty-icon-shadow"
                    x="0"
                    y="-1"
                    width="52"
                    height="57"
                    filterUnits="userSpaceOnUse"
                    colorInterpolationFilters="sRGB"
                >
                    <feFlood floodOpacity="0" result="BackgroundImageFix" />
                    <feColorMatrix
                        in="SourceAlpha"
                        type="matrix"
                        values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                        result="hardAlpha"
                    />
                    <feOffset dy="1" />
                    <feGaussianBlur stdDeviation="1" />
                    <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0" />
                    <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow" />
                    <feColorMatrix
                        in="SourceAlpha"
                        type="matrix"
                        values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                        result="hardAlpha"
                    />
                    <feOffset dy="3" />
                    <feGaussianBlur stdDeviation="1.5" />
                    <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0" />
                    <feBlend mode="normal" in2="effect1_dropShadow" result="effect2_dropShadow" />
                    <feColorMatrix
                        in="SourceAlpha"
                        type="matrix"
                        values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                        result="hardAlpha"
                    />
                    <feOffset dx="1" dy="8" />
                    <feGaussianBlur stdDeviation="2.5" />
                    <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.05 0" />
                    <feBlend mode="normal" in2="effect2_dropShadow" result="effect3_dropShadow" />
                    <feBlend mode="normal" in="SourceGraphic" in2="effect3_dropShadow" result="shape" />
                    <feColorMatrix
                        in="SourceAlpha"
                        type="matrix"
                        values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                        result="hardAlpha"
                    />
                    <feOffset dy="-2" />
                    <feGaussianBlur stdDeviation="1" />
                    <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
                    <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0" />
                    <feBlend mode="normal" in2="shape" result="effect4_innerShadow" />
                </filter>
            </defs>
        </svg>
    );
}
